import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import useSearch from 'hooks/store/useSearch';
import useUserPreferences from 'hooks/useUserPreferences';
import { getBaseEligibility, getResultsEligibility } from 'utils/quickPurchaseEligibility';
import quickPurchaseIconSrc from '../../images/purchases/quick-purchase.png';
import useLoyaltyPrograms from '../../loyalty/context/useLoyaltyPrograms';

/**
 * QuickPurchaseAvailableBanner component.
 * Render an informational banner indicating quick purchase is available.
 * Clicking the banner opens the quick purchase intro modal.
 * Visibility mirrors the exact same conditions as showQuickPurchase in TripItem. (loyalty gate, bulk_ticket route, etc.)
 * @param {object} props - trips, isRoundTrip, isExchange, onClick.
 * @param {Array} props.trips - List of trip objects from search results.
 * @param {boolean} props.isRoundTrip - Whether the current search is round trip.
 * @param {boolean} props.isExchange - Whether the current flow is an exchange.
 * @param {Function} props.onClick - Called when the banner is clicked.
 * @returns {JSX.Element|null} The rendered banner or null.
 */
const QuickPurchaseAvailableBanner = ({ trips, isRoundTrip, isExchange, onClick }) => {
  const { t } = useTranslation('purchase');
  const features = useWhitelabelFeatures();
  const quickPurchaseEnabled = useExperimentalFeature('QUICK_PURCHASE_ENABLED');
  const { userIsLoggedInWithAnyLoyaltyProgram, isQuickPurchaseLoyaltySettled = true } =
    useLoyaltyPrograms();
  const { passengers: searchPassengers, departure: searchDeparture } = useSearch();
  const isBulkTicket = searchDeparture?.routeType === 'bulk_ticket';
  const { passengers: frequentPassengers } = useUserPreferences();

  const { eligible } = getBaseEligibility({
    quickPurchaseEnabled,
    searchPassengers,
    frequentPassengers,
    features,
    isRoundTrip,
    isExchange,
    isBulkTicket,
    isQuickPurchaseLoyaltySettled,
    userIsLoggedInWithAnyLoyaltyProgram,
  });
  const showBanner = getResultsEligibility(trips, eligible, features);

  if (!showBanner) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start ml-auto gap-3 rounded-[20px] border border-gray-200 bg-white p-4 shadow-md w-full lg:w-fit text-left cursor-pointer hover:shadow-lg transition-shadow"
    >
      <img src={quickPurchaseIconSrc} alt="" className="mt-0.5 h-auto w-4 flex-shrink-0" />
      <div>
        <p className="text-sm font-bold text-gray-900">{t('title.quick_purchase_available')}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {t('title.quick_purchase_available_description')}
        </p>
      </div>
    </button>
  );
};

QuickPurchaseAvailableBanner.propTypes = {
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      allowsSeatSelection: PropTypes.bool,
      availability: PropTypes.number,
      hasConnections: PropTypes.bool,
      passengerTypes: PropTypes.array,
    }),
  ).isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  isExchange: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
};

export default QuickPurchaseAvailableBanner;
